import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import {
  DhruvantaMfaRequiredError,
  loginWithDhruvantaPassword,
} from '../../services/dhruvantaSso'

interface LegacyDhruvantaSignInFormProps {
  /** Called after a successful Dhruvanta sign-in (e.g. to close a modal). Navigation is otherwise
   *  driven by the host's `isAuthenticated` guard once the cookie session is seated. */
  onSignedIn?: () => void
}

/**
 * TODO(deprecation): to be deprecated soon in favour of the token-free BFF sign-in
 * (KAN-2036 — `src/components/auth/DhruvantaSignInButton.tsx`, which renders the
 * SHARED `<DhruvantaAuth config={{ posture: 'bff' }}>` card). Do not build new work
 * on this component.
 *
 * WHY IT IS BEING REPLACED: this surface drives the IdP's API-first password login
 * IN THE BROWSER and receives live IdP `accessToken` + `idToken` values into the JS
 * heap before POSTing them to the retail bridge. That breaks the platform's
 * token-free-browser invariant — an XSS on any retail portal could exfiltrate a
 * Dhruvanta identity, not merely a retail session. The BFF broker seals those
 * tokens server-side instead.
 *
 * IT IS RETAINED ONLY as the no-code-change rollback path, reached when
 * `VITE_DHRUVANTA_AUTH_MODE=legacy`. Once the BFF path is proven live this becomes a
 * full `@deprecated` + removal ticket (together with `src/services/dhruvantaSso.ts`
 * and `authApi.signInWithDhruvanta`).
 *
 * ---------------------------------------------------------------------------
 * "Sign in with Dhruvanta" — an inline, API-first sign-in surface (KAN-668 / KAN-747).
 *
 * KAN-747 — this REPLACES the old redirect button. The Dhruvanta IdP is API-first (no hosted login
 * page to redirect to), so this collects the email + password inline and drives the IdP's API-first
 * password login ({@link loginWithDhruvantaPassword}, no redirect), which returns the app-scoped
 * access token + id_token. Those are posted to the retail bridge via
 * {@link useAuth().loginWithDhruvanta}, which seats the retail cookie session. When the IdP demands
 * a second factor, the user is pointed at the retail-native one-time-code sign-in (the IdP accepts
 * an email OTP as a verified factor).
 */
export function LegacyDhruvantaSignInForm({ onSignedIn }: LegacyDhruvantaSignInFormProps = {}) {
  const { loginWithDhruvanta } = useAuth()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // NOTE (KAN-2036): the enable/disable gate moved UP to <DhruvantaSignInButton>,
  // which owns the single VITE_DHRUVANTA_AUTH_MODE decision. This component is
  // reached ONLY when that mode is explicitly `legacy`, so it always renders.

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (loading) return
    setError(null)
    setLoading(true)
    try {
      const { accessToken, idToken } = await loginWithDhruvantaPassword(email.trim(), password)
      await loginWithDhruvanta(accessToken, idToken)
      onSignedIn?.()
    } catch (err) {
      if (err instanceof DhruvantaMfaRequiredError) {
        setError(
          'Your Dhruvanta account uses two-step verification. Use the one-time code option above to sign in.',
        )
      } else {
        setError(err instanceof Error ? err.message : 'Sign-in failed')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="dhruvanta-sso">
      <div className="dhruvanta-sso__divider"><span>or</span></div>
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="dhruvanta-sso__button"
        >
          Sign in with Dhruvanta
        </button>
      ) : (
        // A nested <form> would be invalid HTML inside the host login <form>, so this is a plain
        // block; the inner controls submit via the button's onClick-driven handler.
        <div className="dhruvanta-sso__form">
          <div className="dhruvanta-sso__field">
            <label htmlFor="dhruvanta-email">Dhruvanta email</label>
            <input
              id="dhruvanta-email"
              type="email"
              name="dhruvantaEmail"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              placeholder="you@example.com"
            />
          </div>
          <div className="dhruvanta-sso__field">
            <label htmlFor="dhruvanta-password">Dhruvanta password</label>
            <input
              id="dhruvanta-password"
              type="password"
              name="dhruvantaPassword"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
          </div>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || !email || !password}
            className="dhruvanta-sso__button"
          >
            {loading ? 'Signing in…' : 'Continue with Dhruvanta'}
          </button>
        </div>
      )}
      {error && (
        <p className="dhruvanta-sso__error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

export default LegacyDhruvantaSignInForm
